import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { isEqual } from "lodash";
import { useToasts } from "react-toast-notifications";

import { OverviewProvider } from "@features/overview-v2/context";
import { useOverviewContext } from "@features/overview-v2/hooks";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";

import { CreateSavedViewModal, UpdateSavedViewModal } from "../modals";

const cleanObject = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.keys(obj).reduce((result, key) => {
    const value = obj[key];
    if (value !== null && value !== undefined) {
      result[key as keyof T] = value;
    }
    return result;
  }, {} as Partial<T>);
};

export const SaveSavedViewsMenu = () => {
  const [isUpdatingSavedView, setIsUpdatingSavedView] = useState(false);
  const [isCreatingSavedView, setIsCreatingSavedView] = useState(false);
  const { selectedSavedView, unsavedFilters, createSavedView, tiles } =
    useOverviewContext();

  const isSavedDisabled = useMemo(() => {
    const cleanedSelectedSavedView =
      selectedSavedView?.config.pageConfiguration.tiles?.map(cleanObject);
    const cleanedTiles = tiles?.map(cleanObject);
    const tilesIsEqual = isEqual(cleanedSelectedSavedView, cleanedTiles);
    const noUnsavedFilters =
      unsavedFilters === null || unsavedFilters.size === 0;

    return tilesIsEqual && noUnsavedFilters;
  }, [selectedSavedView, tiles, unsavedFilters]);

  const openUpdateModal = useCallback(() => {
    setIsUpdatingSavedView(true);
  }, []);
  const closeUpdateModal = useCallback(() => {
    setIsUpdatingSavedView(false);
  }, []);
  const openCreateModal = useCallback(() => {
    setIsCreatingSavedView(true);
  }, []);
  const closeCreateModal = useCallback(() => {
    setIsCreatingSavedView(false);
  }, []);

  const toast = useToasts();

  return (
    <div className="relative">
      <Menu>
        {({ open }) => (
          <>
            <MenuButton
              as="button"
              className={clsx("button secondary-button sm font-bold", {
                "text-white bg-cosmic border-cosmic": open,
              })}
            >
              SAVE
              <motion.i
                initial={false}
                animate={{ rotate: open ? 0 : 180 }}
                transition={{ duration: 0.2 }}
                className={clsx("fa-regular fa-sharp fa-chevron-up ml-2", {
                  "text-white": open,
                })}
              />
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <MenuItems
                className={clsx(
                  "z-10 bg-v2-primary text-sm rounded-lg elevation-1 pt-1 pb-2 w-40",
                )}
                anchor="bottom start"
              >
                {!selectedSavedView?.is_default && (
                  <Option
                    isDisabled={isSavedDisabled}
                    onClick={openUpdateModal}
                  >
                    Update existing
                  </Option>
                )}
                <Option onClick={openCreateModal}>Save as new</Option>
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
      <UpdateSavedViewModal
        isOpen={isUpdatingSavedView}
        onClose={closeUpdateModal}
      />
      <CreateSavedViewModal
        isOpen={isCreatingSavedView}
        onClose={closeCreateModal}
        onSubmit={(name: string) =>
          createSavedView(
            name,
            { container: "saved-views-modal", eventType: "submit" },
            {
              errorMessage: "There was an error creating the view.",
              onSuccess: () => {
                closeCreateModal();
                toast.addToast("Saved view created", {
                  appearance: "success",
                });
              },
            },
          )
        }
      />
    </div>
  );
};

const Option = ({
  children,
  isDisabled = false,
  onClick,
}: {
  children: ReactNode;
  isDisabled?: boolean;
  onClick?(): void;
}) => (
  <MenuItem>
    {({ focus }) => {
      return (
        <div
          className={clsx("w-full text-left px-4 h-8 flex items-center", {
            "hover:bg-cosmic-4 cursor-pointer": !isDisabled,
            "opacity-50": isDisabled,
            "bg-cosmic-4": focus,
          })}
          onClick={() => {
            if (!isDisabled) {
              onClick && onClick();
            }
          }}
        >
          {children}
        </div>
      );
    }}
  </MenuItem>
);
